// Package 4, part A (owner, 7. 10. 2026): the guided tutorial. The screen dims, only the element to tap is live, one
// sentence says what to do, and the step ends with the player's real action (on the server). At most one step at a time,
// the rest wait in order; a step that needs the survivor at camp never makes the player wait ("Come back when Sha
// returns."). `Skip` (with a confirm) ends a step for good; its topic stays in the Guide. Only accounts made after the
// deploy get it. The state is on the server (the survivor's onboarding), so a step goes on after a refresh.
// 10. 10. 2026 (owner, deploy7): only the first session is guided: the first run (watched, its report), the find put on,
// the second run and the attribute points; the runs and the attributes open with a small popup that explains them.
// Everything after that is a popup without dimming (schedule.ts: `card`, `NEED_CARDS`).

export type GuidedStepId = 'first_run' | 'watch' | 'report' | 'equip' | 'second_run' | 'attribute';

/**
 * One stage: the element to tap (its `data-guide`; several: the first one on screen, e.g. the side menu on a wide
 * screen and the tab bar on a phone) and the sentence. `{item}` in a target is the step's item.
 */
export interface GuidedStage {
  target: string[];
  text: string;
  /** A screen of many choices (the attributes): no dimming, only the sentence; the action still ends it. */
  soft?: boolean;
  /**
   * 9. 10. 2026 (owner): one thing at a time on one screen: this stage stays (before the later stages on the same
   * screen) until the player taps inside its element (the second run: the stance, then the supplies, then Send).
   */
  untilTap?: boolean;
  /**
   * 10. 10. 2026 (owner): like `untilTap`, but for several taps (a Bandage, then another): the stage stays until the
   * sentence's Done; taps inside its element only do their own work.
   */
  untilDone?: boolean;
}

export interface GuidedStepDef {
  id: GuidedStepId;
  /** From the first to the last: the overlay shows the last stage whose element is on screen. */
  stages: GuidedStage[];
  /** Waits while the survivor is out (its action needs the survivor at camp). */
  atCamp?: boolean;
  /** Done by a tap the client reports (Watch the run). */
  byTap?: boolean;
  /** The Guide topic that stays for a skipped step. */
  guide: string;
  /** 10. 10. 2026 (owner): a small popup before the first stage (what this is), closed with `Got it`. */
  intro?: { title: string; text: string };
}

const NAV = (to: 'map' | 'camp' | 'inventory' | 'survivor'): string[] => [`nav-${to}`];

/** Order and texts: the owner's list. */
export const GUIDED_STEPS: Record<GuidedStepId, GuidedStepDef> = {
  first_run: {
    id: 'first_run',
    guide: 'loop',
    intro: { title: 'Runs', text: 'Your survivor goes out to a zone, searches, fights what comes, and brings home what they found. A run takes real time and goes on while you are away.' },
    stages: [
      { target: NAV('map'), text: 'Open the map to pick where your survivor goes.' },
      { target: ['zone-forest'], text: 'Calder Woods is the safest place to start. Tap it.' },
      // Calder Woods' own (a wide map opens with a zone's panel: another zone's Prepare is not this stage).
      { target: ['zone-prepare-forest'], text: 'Get your survivor ready for the run.' },
      { target: ['order-send'], text: 'Send your survivor out.' },
    ],
  },
  // 9. 10. 2026 (owner): after the send, watch the run (the Sent screen's Watch the run, or the Camp's View route).
  watch: {
    id: 'watch',
    guide: 'loop',
    byTap: true,
    stages: [{ target: ['session-watch', 'camp-view-route'], text: 'Watch your survivor on the run.' }],
  },
  report: {
    id: 'report',
    guide: 'loop',
    atCamp: true,
    // The report is claimed when it is opened (the first run's) or left: opening it is the action.
    stages: [{ target: ['report-open', 'nav-camp'], text: 'Your survivor is back. Read what happened.' }],
  },
  equip: {
    id: 'equip',
    guide: 'slots',
    atCamp: true,
    stages: [
      { target: NAV('inventory'), text: 'Your survivor found something. Open your gear.' },
      { target: ['item-{item}'], text: 'Open what you found.' },
      { target: ['item-equip'], text: 'Put it on.' },
    ],
  },
  // 9. 10. 2026 (owner): the second run, the player's own choices: the stance, then the supplies, then Send.
  second_run: {
    id: 'second_run',
    guide: 'stances',
    atCamp: true,
    stages: [
      { target: NAV('map'), text: 'Time for a second run. Open the map.' },
      { target: ['zone-forest'], text: 'Back to Calder Woods.' },
      { target: ['zone-prepare-forest'], text: 'Get your survivor ready.' },
      { target: ['stances'], text: 'This time you choose. Your stance decides how you meet other survivors: pick one.', untilTap: true },
      { target: ['supplies'], text: 'Bandages heal you on the run. Take as many as you think you need.', untilDone: true },
      { target: ['order-send'], text: 'Send your survivor out.' },
    ],
  },
  attribute: {
    id: 'attribute',
    guide: 'attributes',
    intro: { title: 'Attributes', text: 'Each level gives points to spend. STR hits harder up close and carries more, AGI dodges and strikes first, PER shoots and finds more, VIT adds health, INT makes crafting and repairs cheaper.' },
    stages: [
      { target: NAV('survivor'), text: 'You have attribute points to spend.' },
      { target: ['attr-panel'], text: 'Put your points where your survivor needs them, then save.', soft: true },
    ],
  },
};

/**
 * The steps of a new account's first session, in order (9. 10. 2026, owner: the run watched, then a second run of the
 * player's own choices). The attribute points follow the second run (the server).
 */
export const FIRST_STEPS: GuidedStepId[] = ['first_run', 'watch', 'report', 'equip', 'second_run'];

/** 9. 10. 2026: the first session's steps (since 10. 10. 2026 all of them). */
export const FIRST_SESSION_STEPS: ReadonlySet<GuidedStepId> = new Set<GuidedStepId>([...FIRST_STEPS, 'attribute']);

export interface GuidedState {
  /** The step on now (one at a time), since when, when its first stage was shown, and its item. */
  current: { id: GuidedStepId; since: number; shownAt?: number; item?: string | null } | null;
  /** Waiting steps, in order, with their item. */
  queue: { id: GuidedStepId; item?: string | null }[];
  done: Partial<Record<GuidedStepId, number>>;
  skipped: Partial<Record<GuidedStepId, number>>;
}

export const newGuided = (): GuidedState => ({ current: null, queue: [], done: {}, skipped: {} });

/** Whether a step was ever started, finished, skipped or is waiting. */
export function guidedKnown(g: GuidedState, id: GuidedStepId): boolean {
  return g.current?.id === id || g.queue.some((q) => q.id === id) || g.done[id] != null || g.skipped[id] != null;
}

/** Adds a step (once ever); it starts at once when nothing else is on. */
export function guidedAdd(g: GuidedState, id: GuidedStepId, now: number, item: string | null = null): GuidedState {
  if (guidedKnown(g, id) || !GUIDED_STEPS[id]) return g;
  if (!g.current) return { ...g, current: { id, since: now, item } };
  return { ...g, queue: [...g.queue, { id, item }] };
}

/** The next waiting step goes on. */
function next(g: GuidedState, now: number): GuidedState {
  const [head, ...rest] = g.queue;
  return { ...g, current: head ? { id: head.id, since: now, item: head.item ?? null } : null, queue: rest };
}

/**
 * The player did what a step teaches. The step on now ends (the next one starts); one still waiting is done at once
 * (the player found it alone) and never shows. Returns the state and how long the step took (from its first showing).
 */
export function guidedDone(g: GuidedState, id: GuidedStepId, now: number): { g: GuidedState; was: 'current' | 'queued' | null; ms: number | null } {
  if (g.current?.id === id) {
    const ms = now - (g.current.shownAt ?? g.current.since);
    return { g: next({ ...g, done: { ...g.done, [id]: now } }, now), was: 'current', ms };
  }
  if (g.queue.some((q) => q.id === id)) return { g: { ...g, queue: g.queue.filter((q) => q.id !== id), done: { ...g.done, [id]: now } }, was: 'queued', ms: null };
  return { g, was: null, ms: null };
}

/** 23. 10. 2026 (owner, pilot report): the first session's runs, whose reports open by themselves when they come back. */
export const FIRST_SESSION_RUNS = 2;

/** 23. 10. 2026 (owner, pilot report): the steps that need a run (there is nothing to watch, read or put on without one). */
export const RUN_STEPS: readonly GuidedStepId[] = ['watch', 'report', 'equip', 'second_run'];

/**
 * 23. 10. 2026 (owner, pilot report): `Skip` on the first run's step also skips the steps that need a run, still waiting
 * or on (a player was sent on to "Read your report" with no run and left). The attribute step comes later on its own.
 */
export function guidedSkipRunSteps(g: GuidedState, now: number): GuidedState {
  const skipped = { ...g.skipped };
  for (const id of RUN_STEPS) if (g.done[id] == null && (g.current?.id === id || g.queue.some((q) => q.id === id))) skipped[id] = now;
  const queue = g.queue.filter((q) => !RUN_STEPS.includes(q.id));
  const off = g.current && RUN_STEPS.includes(g.current.id);
  const rest = { ...g, skipped, queue, current: off ? null : g.current };
  return off ? next(rest, now) : rest;
}

/** `Skip` (confirmed): the step on now ends for good; the next one starts. */
export function guidedSkip(g: GuidedState, id: GuidedStepId, now: number): { g: GuidedState; ms: number | null } {
  if (g.current?.id !== id) return { g, ms: null };
  const ms = now - (g.current.shownAt ?? g.current.since);
  return { g: next({ ...g, skipped: { ...g.skipped, [id]: now } }, now), ms };
}

/** The client showed the step's first stage, or its intro was read (its time counts from here). */
export function guidedShown(g: GuidedState, id: GuidedStepId, now: number): GuidedState {
  if (g.current?.id !== id || g.current.shownAt != null) return g;
  return { ...g, current: { ...g.current, shownAt: now } };
}

/**
 * What the client gets: the step on now, its item, whether it waits for the survivor to come back, and whether its intro
 * popup is still to be read (10. 10. 2026).
 */
export interface GuidedDTO {
  id: GuidedStepId;
  item: string | null;
  waiting: boolean;
  intro?: boolean;
}
